import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Drawer, Grid } from 'antd';
import {
    MenuOutlined,
    CloseOutlined,
    UserOutlined,
    DownOutlined,
    CarOutlined,
    HomeOutlined,
    MedicineBoxOutlined,
    ToolOutlined,
    RocketOutlined,
    AuditOutlined,
    SafetyOutlined,
    BugOutlined,
} from '@ant-design/icons';
import logo from '../../assets/logo/logo-cap-assurances.JPG';
import './Navbar.scss';

const { useBreakpoint } = Grid;

const assurancesItems = [
    { key: '/assurances/auto', label: 'Automobile', icon: <CarOutlined /> },
    { key: '/assurances/habitation', label: 'Habitation', icon: <HomeOutlined /> },
    { key: '/assurances/sante', label: 'Sante', icon: <MedicineBoxOutlined /> },
    { key: '/assurances/chantier', label: 'Chantier', icon: <ToolOutlined /> },
    { key: '/assurances/transport', label: 'Transport', icon: <RocketOutlined /> },
    { key: '/assurances/responsabilite-civile', label: 'Responsabilite Civile', icon: <AuditOutlined /> },
];

const agricolesItems = [
    { key: '/assurances/plantation', label: 'Plantation', icon: <SafetyOutlined /> },
    { key: '/assurances/recolte', label: 'Recolte', icon: <SafetyOutlined /> },
    { key: '/assurances/betail', label: 'Betail', icon: <BugOutlined /> },
];

export default function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const screens = useBreakpoint();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const dropdownRef = useRef(null);

    const isMobile = !screens.lg;

    // Scroll effect
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Close dropdown on route change
    useEffect(() => {
        setOpenDropdown(null);
    }, [location.pathname]);

    const goTo = (path) => {
        navigate(path);
        setDrawerOpen(false);
        setOpenDropdown(null);
    };

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    const toggleDropdown = (name) => {
        setOpenDropdown(prev => prev === name ? null : name);
    };

    const DropdownMenu = ({ items, name, columns = 1 }) => (
        <div className={`nav-dropdown ${openDropdown === name ? 'open' : ''}`}>
            <div className={`dropdown-grid cols-${columns}`}>
                {items.map(item => (
                    <div
                        key={item.key}
                        className={`dropdown-item ${location.pathname === item.key ? 'active' : ''}`}
                        onClick={() => goTo(item.key)}
                    >
                        <span className="dropdown-item-icon">{item.icon}</span>
                        <span className="dropdown-item-label">{item.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );

    // Mobile menu items for drawer
    const MobileMenuSection = ({ title, items }) => (
        <div className="mobile-section">
            <div className="mobile-section-title">{title}</div>
            {items.map(item => (
                <div
                    key={item.key}
                    className={`mobile-item ${location.pathname === item.key ? 'active' : ''}`}
                    onClick={() => goTo(item.key)}
                >
                    {item.icon && <span className="mobile-item-icon">{item.icon}</span>}
                    <span>{item.label}</span>
                </div>
            ))}
        </div>
    );

    return (
        <header className={`site-navbar ${scrolled ? 'scrolled' : ''}`}>
            <div className="navbar-inner" ref={dropdownRef}>
                <div className="navbar-logo" onClick={() => goTo('/')}>
                    <img src={logo} alt="CAP Assurances" />
                </div>

                {!isMobile ? (
                    <>
                        <nav className="navbar-nav">
                            <div
                                className={`nav-link ${isActive('/') && !isActive('/a-propos') && !isActive('/assurances') && !isActive('/contact') ? 'active' : ''}`}
                                onClick={() => goTo('/')}
                            >
                                Accueil
                            </div>

                            <div
                                className={`nav-link ${isActive('/a-propos') ? 'active' : ''}`}
                                onClick={() => goTo('/a-propos')}
                            >
                                A propos
                            </div>

                            <div
                                className={`nav-link has-dropdown ${isActive('/assurances') && !agricolesItems.some(i => location.pathname === i.key) ? 'active' : ''}`}
                                onClick={() => toggleDropdown('assurances')}
                                onMouseEnter={() => setOpenDropdown('assurances')}
                            >
                                Nos assurances <DownOutlined className="dropdown-arrow" />
                                <DropdownMenu items={assurancesItems} name="assurances" columns={2} />
                            </div>

                            <div
                                className={`nav-link has-dropdown ${agricolesItems.some(i => location.pathname === i.key) ? 'active' : ''}`}
                                onClick={() => toggleDropdown('agricoles')}
                                onMouseEnter={() => setOpenDropdown('agricoles')}
                            >
                                Risques Agricoles <DownOutlined className="dropdown-arrow" />
                                <DropdownMenu items={agricolesItems} name="agricoles" />
                            </div>

                            <div
                                className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
                                onClick={() => goTo('/contact')}
                            >
                                Contact
                            </div>
                        </nav>

                        <Button
                            type="primary"
                            icon={<UserOutlined />}
                            onClick={() => goTo('/connexion')}
                            className="navbar-cta"
                            size="large"
                        >
                            Espace Client
                        </Button>
                    </>
                ) : (
                    <div className="navbar-mobile-actions">
                        <Button
                            type="primary"
                            icon={<UserOutlined />}
                            onClick={() => goTo('/connexion')}
                            size="small"
                        >
                            Connexion
                        </Button>
                        <button
                            className="hamburger"
                            onClick={() => setDrawerOpen(true)}
                            aria-label="Menu"
                        >
                            <MenuOutlined />
                        </button>
                    </div>
                )}
            </div>

            {/* Mobile Drawer */}
            <Drawer
                title={
                    <div className="drawer-header">
                        <img src={logo} alt="CAP Assurances" className="drawer-logo" />
                    </div>
                }
                placement="right"
                onClose={() => setDrawerOpen(false)}
                open={drawerOpen}
                closeIcon={<CloseOutlined />}
                width={320}
                className="mobile-nav-drawer"
                styles={{ body: { padding: 0 } }}
            >
                <div className="mobile-nav-content">
                    <div
                        className={`mobile-item main ${isActive('/') && !isActive('/a-propos') ? 'active' : ''}`}
                        onClick={() => goTo('/')}
                    >
                        Accueil
                    </div>
                    <div
                        className={`mobile-item main ${isActive('/a-propos') ? 'active' : ''}`}
                        onClick={() => goTo('/a-propos')}
                    >
                        A propos de nous
                    </div>

                    <MobileMenuSection title="Nos assurances" items={assurancesItems} />
                    <MobileMenuSection title="Risques Agricoles" items={agricolesItems} />

                    <div
                        className={`mobile-item main ${isActive('/contact') ? 'active' : ''}`}
                        onClick={() => goTo('/contact')}
                    >
                        Contact
                    </div>

                    <div className="mobile-cta">
                        <Button
                            type="primary"
                            icon={<UserOutlined />}
                            onClick={() => goTo('/connexion')}
                            block
                            size="large"
                        >
                            Espace Client
                        </Button>
                    </div>
                </div>
            </Drawer>
        </header>
    );
}
